import clsx from 'clsx';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';

/**
 * A place in the layout the dock can be rendered into.
 *
 * - `bar`: the row at the bottom of the content column, where the dock lives by default.
 * - `rail`: the column on the right, which the dock takes when the agent is anchored there.
 */
export type AgentDockSlotKind = 'bar' | 'rail';

/**
 * Elements the dock's surfaces are rendered into.
 *
 * The dock is mounted in the application frame so its conversations survive navigation, but it has
 * to *appear* where the layout puts it, beside the sidebar rather than under it. A portal keeps
 * those two facts compatible: the React tree (and so the websockets and state) stays in the frame
 * while the DOM lands in the layout.
 */
const agentDockSlotsAtom = atom<Record<AgentDockSlotKind, HTMLElement | null>>({
    bar: null,
    rail: null,
});

/**
 * Reserve a place for the dock in the layout.
 *
 * Rendered by the layout, so the app gives the dock its own space instead of the dock covering the
 * screen. A slot holding nothing takes none of that space, which is what lets the layout offer one
 * without knowing whether the dock will render there — or at all.
 */
export function AgentDockSlot(props: { kind: AgentDockSlotKind; className?: string }) {
    const { kind, className } = props;
    const setSlots = useSetAtom(agentDockSlotsAtom);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        // First mount wins. An overlay screen renders a second copy of the layout, and the dock can
        // only be portalled into one element — keeping the first keeps it in the screen underneath,
        // where it stays put while the overlay opens and closes.
        let claimed = false;
        setSlots((current) => {
            if (current[kind]) {
                return current;
            }
            claimed = true;
            return { ...current, [kind]: element };
        });

        return () => {
            if (claimed) {
                setSlots((current) =>
                    current[kind] === element ? { ...current, [kind]: null } : current
                );
            }
        };
    }, [setSlots, kind]);

    return <div ref={ref} className={clsx('empty:hidden', className)} />;
}

/**
 * The element the dock should render that surface into, or null before the layout has mounted one
 * (login, onboarding, and the other screens rendered without the app layout — and the rail, which
 * only exists while the agent is anchored).
 */
export function useAgentDockSlot(kind: AgentDockSlotKind): HTMLElement | null {
    return useAtomValue(agentDockSlotsAtom)[kind];
}
